import { ZXCVBNResult } from "zxcvbn";

const ZXCVBN_SRC = "./local/assets/js/zxcvbn.js";

const MIN_COMPLEXITY_SCORE = 3;
const MIN_PASSWORD_LENGTH = 8;
const BASE_WARNING =
  "Use a combination of upper and lowercase letters, digits, and symbols to create a stronger password.";

export const complexityRule = async (
  _,
  model: { password?: string; email?: string }
) => {
  try {
    const userInputs: string[] = [];

    if (model.email != null) {
      userInputs.push(model.email);
      userInputs.push(model.email.split("@")[0]);
    }

    const complexity = await getComplexity(model.password, userInputs);

    if (complexity.score < MIN_COMPLEXITY_SCORE) {
      return {
        complexity: {
          warning: rewriteWarning((complexity as BadPasswordComplexity).warning)
        }
      };
    }
  } catch {
    return null;
  }
};

function rewriteWarning(originalWarning: string): string {
  if (warningRewriteMap[originalWarning] == null) {
    return `${originalWarning}. ${BASE_WARNING}`;
  }

  const { rewrite, mode } = warningRewriteMap[originalWarning];
  switch (mode) {
    case "append":
      return `${BASE_WARNING} ${rewrite}`;
    case "prepend":
      return `${rewrite} ${BASE_WARNING}`;
    case "replace":
      return rewrite;
  }
}

interface WarningRewriteMap {
  [originalMessage: string]: {
    rewrite: string;
    mode: "prepend" | "append" | "replace"; // specifies how to attach rewrite to base message
  };
}

const warningRewriteMap: WarningRewriteMap = {
  "This is a top-10 common password": {
    rewrite: "This is one of the 10 most common passwords.",
    mode: "prepend"
  },
  "This is a top-100 common password": {
    rewrite: "This is one of the 100 most common passwords.",
    mode: "prepend"
  },
  "Common names and surnames are easy to guess": {
    rewrite: "",
    mode: "prepend"
  },
  "This is a very common password": {
    rewrite: "",
    mode: "prepend"
  },
  "Sequences like abc or 6543 are easy to guess": {
    rewrite:
      "Avoid sequences (e.g. 'abcdefg', '12345'), which are easy to guess.",
    mode: "append"
  },
  // tslint:disable-next-line:quotemark
  'Repeats like "abcabcabc" are only slightly harder to guess than "abc"': {
    rewrite:
      "Passwords with repeating characters, like 'abcabcabc', are easy to guess",
    mode: "append"
  },
  "Add another word or two. Uncommon words are better.": {
    rewrite:
      "Use a combination of upper and lowercase letters, digits, and symbols to create a stronger password that is harder for others to guess.",
    mode: "replace"
  }
};

interface BadPasswordComplexity {
  score: 0 | 1 | 2;
  warning: string;
}
interface GoodPasswordComplexity {
  score: 3 | 4; // 4 is most secure, 3 is acceptable
}

type PasswordComplexity = BadPasswordComplexity | GoodPasswordComplexity;

async function getComplexity(
  password: string = "",
  userInputs?: string[]
): Promise<PasswordComplexity> {
  const minimumRequirementsResult = meetsMinimumRequirements(
    password,
    userInputs
  );
  if (minimumRequirementsResult.score === 0) {
    return minimumRequirementsResult;
  } else {
    const zxcvbnScore = await callZxcvbn(password, userInputs);
    const {
      score,
      feedback: { warning, suggestions }
    } = zxcvbnScore;
    return {
      score,
      warning:
        // tslint:disable-next-line:max-line-length
        warning != null && warning.length ? warning : suggestions[0] // arbitrarily choose to show first suggestion if there is no warning provided
    };
  }
}

function meetsMinimumRequirements(
  password?: string,
  userInputs?: string[]
): PasswordComplexity {
  const rules = [
    hasMinimumLength,
    hasAtLeastThreeCharacterClasses,
    doesNotContainUserInputs(userInputs)
  ];

  for (const rule of rules) {
    const result = rule(password);
    if (result.score !== 4) {
      return result;
    }
  }

  return { score: 4 };
}

function hasMinimumLength(password: string = ""): PasswordComplexity {
  if (password.length >= MIN_PASSWORD_LENGTH) {
    return { score: 4 };
  }

  return {
    score: 0,
    warning: `Password must be at least ${MIN_PASSWORD_LENGTH} characters long`
  };
}

function hasAtLeastThreeCharacterClasses(
  password: string = ""
): PasswordComplexity {
  const characterClasses = [
    /[a-z]/, // lowercase
    /[A-Z]/, // uppercase
    /[0-9]/, // number
    /[^a-zA-Z0-9]/ // symbol
  ].filter((regex) => regex.test(password)).length;

  if (characterClasses >= 3) {
    return { score: 4 };
  }

  return {
    score: 0,
    warning:
      "Password must include three of the following: uppercase, lowercase, number, non-alphabetic characters)"
  };
}

function doesNotContainUserInputs(
  userInputs: string[] = []
): (password: string) => PasswordComplexity {
  return (password: string): PasswordComplexity => {
    const passes = userInputs.every((input) => !password.includes(input));
    return passes
      ? { score: 4 }
      : {
          score: 0,
          warning:
            "Password cannot contain your email address or email username"
        };
  };
}

async function callZxcvbn(
  password: string,
  userInputs?: string[]
): Promise<ZXCVBNResult> {
  await loadZxcvbn();

  interface Window {
    zxcvbn: (password: string, userInputs?: string[]) => ZXCVBNResult;
  }

  return window.zxcvbn(password, userInputs);
}

function loadZxcvbn(): Promise<void> {
  return loadScriptWithTimer(ZXCVBN_SRC, 100);
}

function loadScriptWithTimer(
  scriptSrc: string,
  limitMs: number
): Promise<void> {
  return timelimitize(loadScript(scriptSrc), limitMs);
}

let loadScriptCache;
function loadScript(scriptSrc: string): Promise<void> {
  if (loadScriptCache == null) {
    loadScriptCache = new Promise<void>((res, rej) => {
      const script = document.createElement("script");
      script.src = scriptSrc;
      script.onload = () => res();
      script.onerror = (event) => rej(event);
      document.body.appendChild(script);
    });
  }

  return loadScriptCache;
}

function timelimitize<T>(promise: Promise<T>, limitMs: number): Promise<T> {
  const promiseTimer = new Promise<T>((_, rej) =>
    setTimeout(() => rej(new Error("Loading timed out")), limitMs)
  );

  return Promise.race<T>([promise, promiseTimer]);
}
